/* RESPONSIBLE TEAM: team-standalone */
/* eslint-disable @intercom/intercom/no-bare-strings */
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

// Duplicated in apps/teammate-app/src/features/standalone-deploy/api/schema.ts
// In the backend, these are defined in app/lib/standalone/constants/integration_states.rb
export enum IntegrationState {
  NotConfigured = -1,
  Created = 0,
  Paused = 1,
  Enabled = 2,
  TestMode = 3,
  AuthFailed = 4,
}

// Returns the "highest" state for a platform, e.g. if Zendesk enabled for tickets but not for sunshine, this will return IntegrationState.Enabled
export function calculateHighestIntegrationState(
  integrationStates: Array<IntegrationState | undefined>,
) {
  if (integrationStates.includes(IntegrationState.Enabled)) {
    return IntegrationState.Enabled;
  }
  if (integrationStates.includes(IntegrationState.Paused)) {
    return IntegrationState.Paused;
  }
  if (integrationStates.includes(IntegrationState.TestMode)) {
    return IntegrationState.TestMode;
  }
  if (integrationStates.includes(IntegrationState.Created)) {
    return IntegrationState.Created;
  }
  if (integrationStates.includes(IntegrationState.AuthFailed)) {
    return IntegrationState.AuthFailed;
  }
  return IntegrationState.NotConfigured;
}

export enum IntegrationType {
  IntercomSuite = -1,
  ZendeskSunshine = 1,
  ZendeskTickets = 2,
  SalesforceCases = 3,
  IntercomMessenger = 4,
  Api = 5,
  FreshdeskTickets = 6,
  Discord = 7,
  Freshchat = 8,
  HubspotMessenger = 9,
  HubspotTickets = 10,
  CustomHelpdesk = 11,
  ZendeskLiteMessenger = 12,
  ZendeskLiteTickets = 13,
  FrontMessenger = 14,
  SalesforceFinMessenger = 15,
  ZendeskFinMessenger = 19,
}

// In the backend, these are defined in app/lib/standalone/constants/handoff_behaviors.rb
export enum HandoffBehavior {
  NotConfigured = 0,
  ZendeskLiveAgent = 1,
  ZendeskTicket = 2,
  ZendeskHybrid = 3,
  GenericJs = 4,
  GenericUrl = 5,
  GenericCustomAction = 6,
  SalesforceInAppMessaging = 7,
  FreshdeskAgent = 8,
  SalesforceCase = 9,
}

export enum StandalonePlatform {
  Zendesk = 'zendesk',
  Salesforce = 'salesforce',
  Hubspot = 'hubspot',
  Freshdesk = 'freshdesk',
  Front = 'front',
  AmazonConnect = 'amazon_connect',
  HelpScout = 'help_scout',
  ServiceNow = 'servicenow',
  Gorgias = 'gorgias',
  ZohoDesk = 'zoho_desk',
  Unknown = 'unknown',
  CustomHelpdesk = 'custom_helpdesk',
  ZendeskLite = 'zendesk_lite',
}

// Duplicated from apps/embercom/app/objects/environments/connection-types.ts
// In the backend, these are defined in app/lib/standalone/constants/connection_types.rb
export enum ConnectionType {
  TestConnection = -100,
  SuiteConnection = -1,
  ZendeskSunshineApi = 1,
  ZendeskApi = 2,
  SalesforceApi = 3,
  FreshdeskApi = 6,
  FreshchatApi = 8,
  FrontApi = 15,
  HubspotApi = 16,
}

// Translation key suffixes for Settings > Data sync cards.
// These map to existing "new-settings.submenu.data.*" translation entries.
export const ConnectionTypeToDataSyncTranslationKey: Partial<Record<ConnectionType, string>> = {
  [ConnectionType.FreshdeskApi]: 'freshdesk-data',
  [ConnectionType.FreshchatApi]: 'freshchat-data',
  [ConnectionType.SalesforceApi]: 'salesforce-data',
  [ConnectionType.ZendeskApi]: 'zendesk-data',
};

export const StandalonePlatformDisplayName: Record<StandalonePlatform, string> = {
  zendesk: 'Zendesk',
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  freshdesk: 'Freshdesk',
  front: 'Front',
  amazon_connect: 'Amazon Connect',
  help_scout: 'Help Scout',
  servicenow: 'ServiceNow',
  gorgias: 'Gorgias',
  zoho_desk: 'Zoho Desk',
  unknown: 'Generic',
  custom_helpdesk: 'Custom Helpdesk',
  zendesk_lite: 'Zendesk',
};

export const SupportedStandaloneDataSyncPages: ConnectionType[] = [
  ConnectionType.FreshdeskApi,
  ConnectionType.FreshchatApi,
  ConnectionType.SalesforceApi,
  ConnectionType.ZendeskApi,
];

export const ConnectionTypeToPlatform: Partial<Record<ConnectionType, StandalonePlatform>> = {
  [ConnectionType.FreshdeskApi]: StandalonePlatform.Freshdesk,
  [ConnectionType.FreshchatApi]: StandalonePlatform.Freshdesk,
  [ConnectionType.SalesforceApi]: StandalonePlatform.Salesforce,
  [ConnectionType.ZendeskApi]: StandalonePlatform.Zendesk,
};

export const IntegrationTypeToPlatform: Partial<Record<IntegrationType, StandalonePlatform>> = {
  [IntegrationType.ZendeskSunshine]: StandalonePlatform.Zendesk,
  [IntegrationType.ZendeskTickets]: StandalonePlatform.Zendesk,
  [IntegrationType.ZendeskLiteMessenger]: StandalonePlatform.ZendeskLite,
  [IntegrationType.ZendeskLiteTickets]: StandalonePlatform.ZendeskLite,
  [IntegrationType.SalesforceCases]: StandalonePlatform.Salesforce,
  [IntegrationType.ZendeskFinMessenger]: StandalonePlatform.Zendesk,
  [IntegrationType.HubspotMessenger]: StandalonePlatform.Hubspot,
  [IntegrationType.HubspotTickets]: StandalonePlatform.Hubspot,
  [IntegrationType.FreshdeskTickets]: StandalonePlatform.Freshdesk,
  [IntegrationType.Freshchat]: StandalonePlatform.Freshdesk,
  [IntegrationType.CustomHelpdesk]: StandalonePlatform.CustomHelpdesk,
  [IntegrationType.FrontMessenger]: StandalonePlatform.Front,
};

export const SupportedStandalonePlatforms = [
  StandalonePlatform.Zendesk,
  StandalonePlatform.Salesforce,
  StandalonePlatform.Hubspot,
  StandalonePlatform.Freshdesk,
  StandalonePlatform.Front,
  StandalonePlatform.CustomHelpdesk,
  StandalonePlatform.ZendeskLite,
];

export const RESPONSE_PROVIDED_ERRORS = [400, 401, 403, 404, 409, 422, 424];
export const StandalonePlatformToIcon: Record<StandalonePlatform, InterfaceIconName> = {
  zendesk: 'zendesk',
  salesforce: 'salesforce',
  hubspot: 'hubspot',
  freshdesk: 'freshdesk',
  front: 'info',
  amazon_connect: 'info',
  help_scout: 'info',
  servicenow: 'info',
  gorgias: 'info',
  zoho_desk: 'info',
  unknown: 'globe',
  custom_helpdesk: 'info',
  zendesk_lite: 'zendesk',
};

export const VALID_PLATFORM_ICONS = Object.values(StandalonePlatformToIcon).filter(
  Boolean,
) as string[];

export enum ExternalType {
  ZendeskTicket = 1,
  ZendeskComment = 2,
  ZendeskSunshineConversation = 3,
  ZendeskSunshineUser = 4,
  ZendeskSunshineMessage = 5,
  SalesforceCase = 6,
  SalesforceCasePart = 7,
  ZendeskTriggerCategory = 8,
  ZendeskTrigger = 9,
  ZendeskWebhook = 10,
  ZendeskAutomation = 11,
  SalesforceTopic = 12,
  ZendeskUser = 13,
  ZendeskSynchronizationState = 14,
  ZendeskUserField = 15,
  ZendeskCustomTicketField = 16,
  SalesforceUserField = 17,
  SalesforceCaseField = 18,
  ZendeskSunshineFollowUpMessage = 19,
  ZendeskAdmin = 20,
  ZendeskSchedule = 21,
  ApiConversation = 22,
  ApiMessage = 23,
  ApiUser = 24,
  ZendeskOrganizationField = 25,
  ZendeskOrganization = 26,
  SalesforceAgent = 27,
  FreshdeskTriggerRule = 28,
  SalesforceFinCaseField = 29,
  SalesforceLegacyChat = 30,
  SalesforceInAppMessageConversation = 31,
  SalesforceInAppMessagePart = 32,
  SalesforceUser = 33,
  NewZendeskTicket = 34,
  ZendeskCsatSurvey = 35,
  FreshdeskTicket = 36,
  FreshdeskMessage = 37,
  FreshdeskUser = 38,
  SunshineTicketTransfer = 39,
  FreshchatConversation = 40,
  FreshchatMessage = 41,
  FreshchatUser = 42,
  FreshdeskTicketField = 43,
  FreshchatAdmin = 44,
  HubspotConversation = 45,
  HubspotMessage = 46,
  HubspotUser = 47,
  ZendeskSystemTicketField = 48,
  ZendeskTag = 57,
}
